import { Popper } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TimeSelection, TimeSelectionType } from '../../../types';
import { DateRangeIcon } from '../../styles';
import { lightTeal } from '../../theme';
import { showTimeSelection } from '../utils-display';

interface SingleValueTimePeriodTooltipProps {
  timeSelection?: TimeSelection;
}

const StyledPopper = styled(Popper)({
  backgroundColor: lightTeal,
  padding: '8px',
  borderRadius: '5px',
  color: 'white',
  zIndex: 2147483647,
});

const Title = styled('h4')({
  fontSize: '1rem',
  color: 'white',
  margin: '0 0 4px 0',
});

const Divider = styled('hr')({
  margin: '0 0 4px 0',
  opacity: 0.3,
});

const Content = styled('p')({
  fontSize: '0.875rem',
  color: 'white',
  margin: 0,
});

export const SingleValueTimePeriodTooltip = (
  props: SingleValueTimePeriodTooltipProps
) => {
  const { t } = useTranslation();
  const { timeSelection } = props;
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const checkFullscreen = () => {
      setIsFullscreen(document.fullscreenElement !== null);
    };

    document.addEventListener('fullscreenchange', checkFullscreen);
    return () =>
      document.removeEventListener('fullscreenchange', checkFullscreen);
  }, []);

  if (
    !timeSelection ||
    timeSelection.type !== TimeSelectionType.CalendarYearSingleValueByMonths
  ) {
    return null;
  }

  const handleHover = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen(!open);
  };

  const popperContent = (
    <>
      <Title>{t('common:commonValues.misc.timePeriod')}</Title>
      <Divider />
      <Content>{showTimeSelection(timeSelection, t)}</Content>
    </>
  );
  return (
    <>
      <span
        className="inline-flex justify-end align-middle"
        onMouseEnter={handleHover}
        onMouseLeave={() => setOpen(false)}
      >
        <DateRangeIcon />
      </span>
      <StyledPopper
        placement="bottom-start"
        open={open}
        anchorEl={anchorEl}
        container={isFullscreen ? document.fullscreenElement : undefined}
        modifiers={[
          {
            name: 'preventOverflow',
            options: {
              boundary: 'window',
              altAxis: true,
              padding: 8,
            },
          },
          {
            name: 'offset',
            options: {
              offset: [0, 8],
            },
          },
        ]}
      >
        {popperContent}
      </StyledPopper>
    </>
  );
};
